// src/components/onboarding/SpotlightTour.jsx — In-App Interactive SVG Spotlight Tour
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, Sparkles, Check } from "lucide-react";
import api from "../../services/api";
import "./onboarding.css";

const TOUR_CONFIGS = {
  Agent: [
    {
      target: "dashboard-stats",
      path: "/dashboard",
      title: "Key Performance Indicators",
      description: "Monitor your real-time conversion rates, active client inquiries, and monthly automotive sales milestones at a glance."
    },
    {
      target: "dashboard-alerts",
      path: "/dashboard",
      title: "Urgent Action Alerts",
      description: "Immediate warnings for overdue customer follow-ups and unhandled client inquiries requiring priority attention."
    },
    {
      target: "sidebar-nav",
      path: "/dashboard",
      title: "Global Navigation Center",
      description: "Quickly access your Leads Pipeline, Follow-up Workbench, Japanese Car Stock, Duty Attendance, and Performance History."
    },
    {
      target: "leads-tabs",
      path: "/dashboard/leads",
      title: "Lead Pipeline Stages",
      description: "Organize prospects through sequential stages: Fresh, Contacted, Proposal Shared, Active Negotiation, and Won/Lost."
    },
    {
      target: "followups-workbench",
      path: "/dashboard/my-followups",
      title: "Daily Follow-up Workbench",
      description: "Your daily mission control. Call clients, update CRM negotiation remarks, and log customer feedback promptly."
    },
    {
      target: "stock-inventory",
      path: "/dashboard/stock-list",
      title: "Japanese Vehicle Inventory",
      description: "Browse verified inventory with full engine specs, CIF/FOB pricing, auction sheets, and direct website links to send to buyers."
    },
    {
      target: "attendance-tracker",
      path: "/dashboard/attendance",
      title: "Biometric Attendance & Breaks",
      description: "Clock in for duty, record lunch or tea breaks within daily allocated limits, and track verified monthly work hours."
    },
    {
      target: "chat-dock-launcher",
      path: "/dashboard",
      title: "Team Chat & AI Assistant",
      description: "Instant messaging with colleagues and managers, private DMs, and on-demand CRM intelligence from PlatinumBot."
    }
  ],
  Manager: [
    {
      target: "dashboard-stats",
      path: "/dashboard",
      title: "Team Overview & Velocity",
      description: "Aggregate team performance metrics, lead conversion velocity, and overall department target fulfillment."
    },
    {
      target: "dashboard-alerts",
      path: "/dashboard",
      title: "Bottlenecks & Overdue Inquiries",
      description: "Identify unattended inquiries across your agents, monitor stalled deals, and maintain rigorous SLA response times."
    },
    {
      target: "leads-table",
      path: "/dashboard/leads",
      title: "Lead Governance & Allocation",
      description: "Distribute fresh incoming inquiries, reallocate uncontacted leads, and inspect full customer interaction histories."
    },
    {
      target: "sidebar-nav",
      path: "/dashboard",
      title: "Team Oversight Navigation",
      description: "Navigate to Team Portfolio, Agent Leaderboard rankings, Sales Tracking, and Attendance Monitoring."
    },
    {
      target: "attendance-tracker",
      path: "/dashboard/attendance",
      title: "Staff Duty & Break Monitor",
      description: "Real-time visibility into which agents are on duty, on break, or absent, with authority to reset stuck break timers."
    },
    {
      target: "chat-dock-launcher",
      path: "/dashboard",
      title: "Team Coordination & DMs",
      description: "Direct real-time communications with sales reps, department group channels, and automated shift summaries."
    }
  ],
  Admin: [
    {
      target: "dashboard-stats",
      path: "/dashboard",
      title: "Executive Health & Metrics",
      description: "Full enterprise visibility into total company pipeline value, gross sales conversions, and active staff velocity."
    },
    {
      target: "sidebar-nav",
      path: "/dashboard",
      title: "Administrative Control Center",
      description: "Govern user accounts, role permissions, brand multi-tenancy, payroll calculations, and comprehensive audit logs."
    },
    {
      target: "payroll-admin",
      path: "/dashboard/payroll",
      title: "Automated Payroll & Commissions",
      description: "Review monthly base salaries, automatically computed sales commissions, deductions, and generate official pay slips."
    },
    {
      target: "stock-inventory",
      path: "/dashboard/stock-list",
      title: "Global Stock & Inventory Sync",
      description: "Manage available vehicle inventory, brand allocation, auction imports, and live website synchronization."
    },
    {
      target: "chat-dock-launcher",
      path: "/dashboard",
      title: "Company Communication & AI Settings",
      description: "Manage company-wide announcement channels, enforce communication standards, and adjust PlatinumBot parameters."
    }
  ]
};

export default function SpotlightTour({ role = "Agent", onComplete }) {
  const navigate = useNavigate();
  const steps = useMemo(() => TOUR_CONFIGS[role] || TOUR_CONFIGS.Agent, [role]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 120, left: 120 });

  const currentStep = steps[currentIndex];

  const calculateTooltipPosition = useCallback((rect) => {
    const tooltipWidth = 380;
    const tooltipHeight = 220;
    const margin = 16;
    const pad = 12;

    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let top = 0;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;

    if (left < margin) left = margin;
    if (left + tooltipWidth > viewportW - margin) {
      left = viewportW - tooltipWidth - margin;
    }

    if (rect.bottom + tooltipHeight + pad < viewportH - margin) {
      top = rect.bottom + pad;
    } else if (rect.top - tooltipHeight - pad > margin) {
      top = rect.top - tooltipHeight - pad;
    } else {
      top = Math.max(margin, viewportH / 2 - tooltipHeight / 2);
    }

    setTooltipPos({ top, left });
  }, []);

  const updateSpotlight = useCallback(() => {
    if (!currentStep) return;

    // Check if route matches; if not, navigate with ?tour=true query
    if (window.location.pathname !== currentStep.path) {
      navigate(`${currentStep.path}?tour=true`);
    }

    let attempts = 0;
    const checkElement = () => {
      attempts++;
      const el = document.querySelector(`[data-tour="${currentStep.target}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        const rect = el.getBoundingClientRect();
        setTargetRect(rect);
        calculateTooltipPosition(rect);
      } else if (attempts < 6) {
        setTimeout(checkElement, 250);
      } else {
        // Fallback centered position
        setTargetRect(null);
        setTooltipPos({
          top: window.innerHeight / 2 - 110,
          left: window.innerWidth / 2 - 190
        });
      }
    };

    checkElement();
  }, [currentStep, navigate, calculateTooltipPosition]);

  useEffect(() => {
    updateSpotlight();
    window.addEventListener("resize", updateSpotlight);
    return () => window.removeEventListener("resize", updateSpotlight);
  }, [updateSpotlight]);

  const handleNext = () => {
    if (currentIndex < steps.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      finishTour();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const finishTour = async () => {
    try {
      await api.post("/onboarding/tutorial-complete");
    } catch (_) {}
    if (onComplete) onComplete();
  };

  if (!currentStep) return null;

  const pad = 8;
  const rx = 10;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  return (
    <>
      {/* SVG Spotlight Cutout Backdrop */}
      <svg className="tour-svg-backdrop" width="100%" height="100%">
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width={vw} height={vh} fill="#ffffff" />
            {targetRect && (
              <rect
                x={Math.max(0, targetRect.left - pad)}
                y={Math.max(0, targetRect.top - pad)}
                width={Math.min(vw, targetRect.width + pad * 2)}
                height={Math.min(vh, targetRect.height + pad * 2)}
                rx={rx}
                ry={rx}
                fill="#000000"
              />
            )}
          </mask>
        </defs>

        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(5, 7, 12, 0.78)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {/* Target Glow Pulse Ring */}
      {targetRect && (
        <div
          className="tour-pulse-ring"
          style={{
            top: `${Math.max(0, targetRect.top - pad)}px`,
            left: `${Math.max(0, targetRect.left - pad)}px`,
            width: `${targetRect.width + pad * 2}px`,
            height: `${targetRect.height + pad * 2}px`,
            borderRadius: `${rx}px`
          }}
        />
      )}

      {/* Interactive Tooltip Card */}
      <div
        className="tour-tooltip-card"
        style={{
          top: `${tooltipPos.top}px`,
          left: `${tooltipPos.left}px`
        }}
      >
        <div className="tour-step-counter">
          <span>
            <Sparkles size={13} style={{ marginRight: 5, verticalAlign: "middle" }} />
            STEP {currentIndex + 1} OF {steps.length}
          </span>
          <span style={{ color: "var(--muted)" }}>Role: {role}</span>
        </div>

        <h4 className="tour-tooltip-title">{currentStep.title}</h4>
        <p className="tour-tooltip-body">{currentStep.description}</p>

        <div className="tour-tooltip-footer">
          <div className="tour-dots-row">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`tour-dot ${i === currentIndex ? "active" : ""}`}
              />
            ))}
          </div>

          <div className="tour-nav-btns">
            <button
              type="button"
              className="tour-btn-mini skip"
              onClick={finishTour}
              title="Skip remaining tutorial steps"
            >
              Skip Tour
            </button>

            {currentIndex > 0 && (
              <button
                type="button"
                className="tour-btn-mini prev"
                onClick={handlePrev}
              >
                <ChevronLeft size={14} /> Back
              </button>
            )}

            <button
              type="button"
              className="tour-btn-mini next"
              onClick={handleNext}
            >
              {currentIndex === steps.length - 1 ? (
                <>
                  <Check size={14} /> Finish Tour
                </>
              ) : (
                <>
                  Next <ChevronRight size={14} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
